import * as THREE from 'three';
import { damp } from '../../core/math';
import type { CharacterLook } from '../../state/types';
import { box, cyl, mat, mesh, sphere, texMat } from '../materials';
import { camoTexture, leopardTexture, plaidTexture } from '../textures';

const HIP = 0.85;

export interface CharacterRig {
  root: THREE.Group;
  /** Pivots at the hips: used for bobbing, leaning and stunt flips. */
  body: THREE.Group;
  torso: THREE.Group;
  head: THREE.Group;
  armL: THREE.Group;
  armR: THREE.Group;
  legL: THREE.Group;
  legR: THREE.Group;
  kneeL: THREE.Group;
  kneeR: THREE.Group;
  /** Beer can held in the right hand while drinking. */
  can: THREE.Mesh;
  /** Attach point above the head for carried junk. */
  carry: THREE.Group;
  /** Attach points in each hand (darts, a cornhole bag, a catfish...). */
  handL: THREE.Group;
  handR: THREE.Group;
  /** The rifle that appears when someone shoulders it (pose 'aim'). */
  rifle: THREE.Group;
  /** Crocs heel straps: relaxed mode vs. sport mode. */
  straps: THREE.Group[];
  look: CharacterLook;
}

/**
 * A lever-action deer rifle with a scope, built along +z (muzzle forward), the grip at
 * the origin. Used in third person and, bigger, as your first-person view model.
 */
export function buildRifle(view = false): THREE.Group {
  const g = new THREE.Group();
  const wood = mat(view ? '#9a6234' : '#7a4a26', { rough: 0.55 });
  const steel = mat(view ? '#50555c' : '#2a2c30', { rough: 0.35, metal: view ? 0.5 : 0.7 });
  g.add(box(0.05, 0.1, 0.32, wood, 0, -0.03, -0.2));
  g.add(box(0.045, 0.07, 0.16, steel, 0, 0.0, 0.04));
  g.add(cyl(0.012, 0.012, 0.56, steel, 0, 0.02, 0.38, 6).rotateX(Math.PI / 2));
  g.add(box(0.04, 0.04, 0.3, wood, 0, -0.025, 0.28));
  g.add(cyl(0.015, 0.015, 0.2, steel, 0, 0.07, 0.05, 8).rotateX(Math.PI / 2));
  for (const z of [-0.04, 0.14]) g.add(cyl(0.021, 0.021, 0.035, steel, 0, 0.07, z, 8).rotateX(Math.PI / 2));
  g.add(box(0.012, 0.03, 0.02, steel, 0, 0.045, 0.05));
  g.add(box(0.012, 0.03, 0.08, steel, 0, -0.06, 0.02));
  g.traverse((o) => (o.userData.noBatch = true));
  return g;
}

function shade(hex: string, amount: number): string {
  const c = new THREE.Color(hex);
  c.lerp(new THREE.Color(amount > 0 ? '#ffffff' : '#000000'), Math.abs(amount));
  return `#${c.getHexString()}`;
}

function topMaterial(look: CharacterLook): THREE.Material {
  switch (look.top) {
    case 'camo':
      return texMat(camoTexture());
    case 'flannel':
      return texMat(plaidTexture(look.topColor));
    case 'leopard':
      return texMat(leopardTexture());
    case 'shirtless':
      // Farmer's tan: the torso is a shade paler than the arms.
      return mat(shade(look.skin, 0.18));
    default:
      return mat(look.topColor);
  }
}

export function buildCharacter(look: CharacterLook): CharacterRig {
  const woman = look.body === 'woman';
  const skin = mat(look.skin);
  const skinDark = mat(shade(look.skin, -0.12));
  const hair = mat(look.hairColor, { rough: 0.95 });
  const top = topMaterial(look);
  const bottom = mat(look.bottomColor);
  const shoe = mat(look.shoeColor, { rough: 0.6 });
  const dark = mat('#1b1b1b');
  // Her build: narrower shoulders, wider hips, slimmer arms and legs.
  const B = woman
    ? {
        legX: 0.105,
        legW: 0.155,
        legD: 0.17,
        shinW: 0.135,
        shinD: 0.15,
        shoulder: 0.245,
        armW: 0.1,
        armD: 0.11,
      }
    : {
        legX: 0.115,
        legW: 0.17,
        legD: 0.19,
        shinW: 0.155,
        shinD: 0.17,
        shoulder: 0.3,
        armW: 0.13,
        armD: 0.14,
      };

  const root = new THREE.Group();
  root.name = 'character';
  const body = new THREE.Group();
  body.position.y = HIP;
  root.add(body);
  const inner = new THREE.Group();
  inner.position.y = -HIP;
  body.add(inner);

  // --- legs (hip pivot -> knee pivot) ---
  const shorts = look.bottom === 'jorts';
  const skirt = look.bottom === 'skirt';
  // Daisy Dukes stop well short of where jorts do.
  const shortsLen = woman ? 0.18 : 0.42;
  const makeLeg = (side: number): [THREE.Group, THREE.Group, THREE.Group] => {
    const leg = new THREE.Group();
    leg.position.set(side * B.legX, HIP, 0);
    if (skirt) leg.add(box(B.legW, 0.42, B.legD, skin, 0, -0.2, 0));
    else if (shorts && woman) {
      leg.add(box(B.legW, shortsLen, B.legD, bottom, 0, 0.01 - shortsLen / 2, 0));
      leg.add(box(B.legW, 0.42 - shortsLen, B.legD, skin, 0, 0.01 - shortsLen - (0.42 - shortsLen) / 2, 0));
    } else leg.add(box(B.legW, 0.42, B.legD, bottom, 0, -0.2, 0));
    if (shorts)
      leg.add(
        box(
          B.legW + 0.008,
          0.035,
          B.legD + 0.008,
          mat(shade(look.bottomColor, 0.35)),
          0,
          0.02 - shortsLen,
          0,
        ),
      );
    const knee = new THREE.Group();
    knee.position.y = -0.41;
    leg.add(knee);
    knee.add(box(B.shinW, 0.4, B.shinD, shorts || skirt ? skin : bottom, 0, -0.2, 0));
    const strapGroup = new THREE.Group();
    switch (look.shoes) {
      case 'boots':
        knee.add(box(0.2, 0.17, 0.32, shoe, 0, -0.36, 0.04));
        knee.add(box(0.21, 0.03, 0.33, dark, 0, -0.43, 0.04));
        break;
      case 'sneakers':
        knee.add(box(0.19, 0.1, 0.31, shoe, 0, -0.39, 0.05));
        knee.add(box(0.2, 0.03, 0.32, mat('#f5f5f5'), 0, -0.43, 0.05));
        break;
      case 'slippers':
        knee.add(box(0.21, 0.1, 0.32, mat(look.shoeColor, { rough: 1 }), 0, -0.39, 0.05));
        break;
      default: {
        // Crocs, naturally.
        knee.add(box(0.21, 0.11, 0.32, shoe, 0, -0.385, 0.05));
        for (let i = 0; i < 3; i++)
          knee.add(box(0.03, 0.005, 0.03, dark, -0.05 + i * 0.05, -0.327, 0.12, { cast: false }));
        strapGroup.position.set(0, -0.36, -0.08);
        strapGroup.add(box(0.225, 0.025, 0.11, shoe, 0, 0.02, 0.055, { cast: false }));
        knee.add(strapGroup);
      }
    }
    return [leg, knee, strapGroup];
  };
  const [legL, kneeL, strapL] = makeLeg(-1);
  const [legR, kneeR, strapR] = makeLeg(1);
  inner.add(legL, legR);
  if (skirt) {
    // Hangs from the hips, so the legs swing underneath it.
    const s = cyl(0.215, 0.29, 0.36, bottom, 0, HIP - 0.13, 0, 10, { open: true });
    s.scale.z = 0.72;
    inner.add(s);
  }

  // --- torso ---
  const torso = new THREE.Group();
  torso.position.y = HIP;
  inner.add(torso);
  const T = (m: THREE.Mesh): THREE.Mesh => {
    m.position.y -= HIP;
    torso.add(m);
    return m;
  };
  const tube = look.top === 'tube';
  const bikini = woman && look.top === 'shirtless';
  // A tube top or a bikini leaves the shoulders and the midriff out.
  const bare = tube || bikini ? skin : top;
  const band = tube || bikini ? mat(look.topColor) : top;
  /** How far the front of the chest sits from the spine (for things stuck on the front). */
  let chestZ: number;
  if (woman) {
    T(box(0.42, 0.2, 0.26, bottom, 0, HIP + 0.02, 0));
    T(box(0.34, 0.24, 0.22, bare, 0, 1.05, 0));
    T(box(0.38, 0.28, 0.23, bare, 0, 1.31, 0));
    for (const sx of [-1, 1]) {
      const cup = T(sphere(0.072, band, sx * 0.066, 1.28, 0.1, 12));
      cup.scale.set(1.05, 0.85, bikini ? 0.75 : 0.62);
    }
    if (tube) T(box(0.39, 0.13, 0.24, band, 0, 1.28, 0));
    if (bikini)
      for (const sx of [-1, 1]) {
        const strap = T(box(0.018, 0.16, 0.012, band, sx * 0.075, 1.39, 0.118, { cast: false }));
        strap.rotation.z = sx * -0.25;
      }
    chestZ = 0.116;
  } else {
    T(box(0.4, 0.16, 0.24, bottom, 0, HIP + 0.02, 0));
    T(box(0.46, 0.52, 0.26, bare, 0, 1.19, 0));
    if (tube) T(box(0.47, 0.15, 0.27, band, 0, 1.3, 0));
    chestZ = 0.131;
  }
  const belly = woman
    ? T(sphere(0.16, bare, 0, 0.99, 0.05 + look.belly * 0.04, 14))
    : T(sphere(0.2, bare, 0, 1.04, 0.06 + look.belly * 0.05, 14));
  // Hers stays flat until she's put some real beer into it.
  if (woman)
    belly.scale.set(1 + look.belly * 0.2, 0.85 + look.belly * 0.3, Math.max(0.2, look.belly * 1.1 - 0.05));
  else belly.scale.set(1.05 + look.belly * 0.2, 0.9 + look.belly * 0.3, 0.55 + look.belly * 0.75);
  if (look.top === 'tank' || look.top === 'camo')
    T(
      woman
        ? box(0.16, 0.09, 0.012, skin, 0, 1.405, 0.121, { cast: false })
        : box(0.2, 0.12, 0.012, skin, 0, 1.39, 0.131, { cast: false }),
    );
  if (look.top === 'tracksuit') {
    if (woman) {
      T(box(0.02, 0.24, 0.012, mat('#ffffff'), 0, 1.05, 0.116, { cast: false }));
      T(box(0.02, 0.08, 0.012, mat('#ffffff'), 0, 1.41, 0.121, { cast: false }));
    } else T(box(0.025, 0.5, 0.012, mat('#ffffff'), 0, 1.19, 0.131, { cast: false }));
  }
  if (look.top === 'nascar') {
    ['#e03a3a', '#2b6cb0', '#f5d90a'].forEach((c, i) =>
      T(
        woman
          ? box(0.08, 0.05, 0.012, mat(c), i % 2 === 0 ? -0.08 : 0.08, 1.12 - i * 0.07, 0.116, {
              cast: false,
            })
          : box(0.1, 0.06, 0.012, mat(c), i % 2 === 0 ? -0.11 : 0.11, 1.3 - i * 0.1, chestZ, { cast: false }),
      ),
    );
  }
  if (look.bottom === 'overalls') {
    if (woman) {
      T(box(0.28, 0.26, 0.02, bottom, 0, 1.08, 0.12));
      T(box(0.04, 0.3, 0.02, bottom, -0.15, 1.3, 0.125, { cast: false }));
      T(box(0.04, 0.3, 0.02, bottom, 0.15, 1.3, 0.125, { cast: false }));
      T(box(0.3, 0.38, 0.02, bottom, 0, 1.17, -0.126, { cast: false }));
    } else {
      T(box(0.3, 0.28, 0.025, bottom, 0, 1.12, 0.135));
      T(box(0.05, 0.36, 0.02, bottom, -0.12, 1.3, 0.136, { cast: false }));
      T(box(0.05, 0.36, 0.02, bottom, 0.12, 1.3, 0.136, { cast: false }));
      T(box(0.32, 0.4, 0.02, bottom, 0, 1.2, -0.136, { cast: false }));
    }
  }
  T(woman ? box(0.1, 0.08, 0.1, skin, 0, 1.48, 0) : box(0.12, 0.08, 0.12, skin, 0, 1.48, 0));

  // --- arms ---
  const longSleeves = look.top === 'flannel' || look.top === 'tracksuit' || look.top === 'nascar';
  const shortSleeves = look.top === 'tee' || look.top === 'leopard';
  const handY = woman ? -0.6 : -0.64;
  const makeArm = (side: number): THREE.Group => {
    const arm = new THREE.Group();
    arm.position.set(side * B.shoulder, 1.4 - HIP, 0);
    arm.add(box(B.armW, woman ? 0.53 : 0.56, B.armD, longSleeves ? top : skin, 0, woman ? -0.255 : -0.27, 0));
    if (shortSleeves) arm.add(box(B.armW + 0.02, 0.18, B.armD + 0.02, top, 0, -0.07, 0));
    if (look.top === 'tracksuit')
      arm.add(box(0.012, 0.5, 0.03, mat('#ffffff'), side * (B.armW / 2 + 0.001), -0.27, 0, { cast: false }));
    arm.add(
      woman
        ? box(0.095, 0.11, 0.1, skin, 0, handY + 0.04, 0)
        : box(0.11, 0.12, 0.12, skin, 0, handY + 0.04, 0),
    );
    return arm;
  };
  const armL = makeArm(-1);
  const armR = makeArm(1);
  torso.add(armL, armR);

  const can = cyl(0.034, 0.034, 0.12, mat('#2f6fd6', { rough: 0.35, metal: 0.5 }), 0, handY - 0.02, 0.06, 10);
  can.add(cyl(0.035, 0.035, 0.02, mat('#d9dde0', { rough: 0.3, metal: 0.7 }), 0, 0.06, 0, 10));
  can.visible = false;
  armR.add(can);
  const handL = new THREE.Group();
  handL.position.set(0, handY, 0.02);
  armL.add(handL);
  const handR = new THREE.Group();
  handR.position.set(0, handY, 0.02);
  armR.add(handR);
  const rifle = buildRifle();
  // Held in the right hand, barrel along the arm.
  rifle.rotation.x = Math.PI / 2;
  rifle.position.set(0, 0.12, 0.02);
  rifle.visible = false;
  handR.add(rifle);

  // --- head ---
  const head = new THREE.Group();
  head.position.y = 1.51 - HIP;
  torso.add(head);
  head.add(box(0.29, 0.31, 0.29, skin, 0, 0.155, 0));
  head.add(box(0.04, 0.045, 0.01, dark, -0.065, 0.18, 0.146, { cast: false }));
  head.add(box(0.04, 0.045, 0.01, dark, 0.065, 0.18, 0.146, { cast: false }));
  head.add(box(0.05, 0.07, 0.05, skinDark, 0, 0.13, 0.16, { cast: false }));
  head.add(box(0.03, 0.08, 0.06, skin, -0.155, 0.15, 0));
  head.add(box(0.03, 0.08, 0.06, skin, 0.155, 0.15, 0));
  if (woman) {
    // Lashes (two little ticks off the outside corner), lipstick and a pair of gold hoops.
    for (const sx of [-1, 1]) {
      for (const [x, y, tilt] of [
        [0.083, 0.207, 0.55],
        [0.07, 0.21, 0.2],
      ] as const) {
        const lash = box(0.008, 0.022, 0.012, dark, sx * x, y, 0.149, { cast: false });
        lash.rotation.z = -sx * tilt;
        head.add(lash);
      }
      const hoop = mesh(
        new THREE.TorusGeometry(0.032, 0.007, 6, 16),
        mat('#e8c35a', { metal: 0.7, rough: 0.3 }),
        sx * 0.168,
        0.078,
        0,
        { cast: false },
      );
      hoop.rotation.y = Math.PI / 2;
      head.add(hoop);
    }
    head.add(box(0.075, 0.022, 0.012, mat('#b5304a', { rough: 0.4 }), 0, 0.07, 0.147, { cast: false }));
  } else head.add(box(0.08, 0.015, 0.01, mat('#7a2e2e'), 0, 0.07, 0.146, { cast: false }));

  const hatted = look.hat !== 'none';
  /** A cap of hair on top, unless a hat's already there. */
  const cap = (h = 0.07, y = 0.335): void => {
    if (!hatted) head.add(box(0.31, h, 0.31, hair, 0, y, 0));
  };
  switch (look.hair) {
    case 'mullet':
      cap();
      head.add(box(0.31, 0.05, 0.03, hair, 0, 0.29, 0.15));
      head.add(box(0.02, 0.12, 0.25, hair, -0.155, 0.25, -0.02));
      head.add(box(0.02, 0.12, 0.25, hair, 0.155, 0.25, -0.02));
      // Party in the back.
      head.add(box(0.3, 0.4, 0.075, hair, 0, 0.1, -0.175));
      break;
    case 'rattail':
      cap(0.05, 0.33);
      head.add(box(0.035, 0.34, 0.03, hair, 0, -0.02, -0.16));
      break;
    case 'teased':
      head.add(box(0.42, 0.2, 0.4, hair, 0, 0.36, -0.01));
      head.add(box(0.06, 0.28, 0.3, hair, -0.18, 0.19, -0.03));
      head.add(box(0.06, 0.28, 0.3, hair, 0.18, 0.19, -0.03));
      head.add(box(0.38, 0.32, 0.08, hair, 0, 0.17, -0.18));
      break;
    case 'buzz':
      cap(0.035, 0.32);
      break;
    case 'bald':
      head.add(box(0.02, 0.07, 0.22, hair, -0.152, 0.2, -0.03));
      head.add(box(0.02, 0.07, 0.22, hair, 0.152, 0.2, -0.03));
      head.add(box(0.3, 0.07, 0.02, hair, 0, 0.2, -0.152));
      break;
    case 'long':
      cap();
      // Bangs, curtains down past the ears, and a sheet of it down the back.
      head.add(box(0.31, 0.06, 0.03, hair, 0, 0.285, 0.152));
      head.add(box(0.032, 0.36, 0.25, hair, -0.158, 0.13, -0.025));
      head.add(box(0.032, 0.36, 0.25, hair, 0.158, 0.13, -0.025));
      head.add(box(0.31, 0.52, 0.07, hair, 0, 0.05, -0.177));
      break;
    case 'ponytail': {
      cap(0.06, 0.33);
      head.add(box(0.025, 0.1, 0.24, hair, -0.156, 0.26, -0.02));
      head.add(box(0.025, 0.1, 0.24, hair, 0.156, 0.26, -0.02));
      head.add(box(0.3, 0.16, 0.05, hair, 0, 0.24, -0.165));
      head.add(box(0.1, 0.06, 0.05, mat('#ff6fa8', { rough: 1 }), 0, 0.27, -0.2));
      const tail = box(0.09, 0.3, 0.07, hair, 0, 0.12, -0.235);
      tail.rotation.x = 0.18;
      head.add(tail);
      break;
    }
    case 'bun':
      cap(0.06, 0.33);
      head.add(box(0.025, 0.1, 0.24, hair, -0.156, 0.26, -0.02));
      head.add(box(0.025, 0.1, 0.24, hair, 0.156, 0.26, -0.02));
      head.add(box(0.3, 0.16, 0.05, hair, 0, 0.24, -0.165));
      // On top, or poking out under the back of the hat.
      head.add(hatted ? sphere(0.08, hair, 0, 0.3, -0.21, 10) : sphere(0.09, hair, 0, 0.42, -0.04, 10));
      break;
  }

  if (look.beard !== 'none') {
    const beard = mat(look.hair === 'bald' ? look.hairColor : shade(look.hairColor, -0.05), { rough: 1 });
    head.add(box(0.15, 0.035, 0.025, beard, 0, 0.095, 0.157, { cast: false }));
    if (look.beard === 'goatee') head.add(box(0.08, 0.07, 0.03, beard, 0, 0.025, 0.148, { cast: false }));
    if (look.beard === 'full') {
      head.add(box(0.31, 0.13, 0.06, beard, 0, 0.035, 0.125));
      head.add(box(0.02, 0.15, 0.2, beard, -0.152, 0.08, 0.02, { cast: false }));
      head.add(box(0.02, 0.15, 0.2, beard, 0.152, 0.08, 0.02, { cast: false }));
    }
  }

  if (look.shades)
    head.add(
      box(0.27, 0.06, 0.02, mat('#0b0b0b', { rough: 0.2, metal: 0.3 }), 0, 0.185, 0.157, { cast: false }),
    );

  const hatMat = mat(look.hatColor);
  if (look.hat === 'trucker' || look.hat === 'backwards') {
    const dir = look.hat === 'trucker' ? 1 : -1;
    head.add(box(0.31, 0.12, 0.31, hatMat, 0, 0.37, 0));
    head.add(box(0.312, 0.11, 0.012, mat('#f4f4f0'), 0, 0.37, dir * 0.156, { cast: false }));
    head.add(box(0.28, 0.02, 0.16, hatMat, 0, 0.315, dir * 0.22));
  } else if (look.hat === 'cowboy') {
    head.add(cyl(0.3, 0.3, 0.02, hatMat, 0, 0.33, 0, 16));
    head.add(cyl(0.13, 0.16, 0.17, hatMat, 0, 0.42, 0, 12));
  }

  const carry = new THREE.Group();
  carry.position.y = 2.15 - HIP;
  torso.add(carry);

  root.scale.setScalar(look.height);
  root.traverse((o) => {
    if ((o as THREE.Mesh).isMesh) o.userData.noBatch = true;
  });

  return {
    root,
    body,
    torso,
    head,
    armL,
    armR,
    legL,
    legR,
    kneeL,
    kneeR,
    can,
    carry,
    handL,
    handR,
    rifle,
    straps: look.shoes === 'crocs' ? [strapL, strapR] : [],
    look,
  };
}

export type Pose =
  | 'stand'
  | 'sit'
  | 'sitdrink'
  | 'rock'
  | 'drink'
  | 'carry'
  | 'push'
  | 'wave'
  | 'cheer'
  | 'fallen'
  | 'eat'
  | 'aim'
  | 'ride'
  | 'armwrestle'
  | 'kneel'
  | 'throw'
  | 'pull'
  | 'scoot'
  | 'scootdrink';

export interface AnimInput {
  dt: number;
  time: number;
  /** Horizontal speed in m/s; drives the walk cycle. */
  speed: number;
  pose: Pose;
  sprinting?: boolean;
  airborne?: boolean;
  /** Height of the seat when sitting (meters). */
  seat?: number;
}

/** Procedural animation by easing each joint toward a per-pose target. */
export class CharacterAnimator {
  private phase = 0;
  private sport = 0;
  /** The most recent input (multiplayer mirrors it to other players). */
  last: Readonly<AnimInput> | null = null;

  constructor(readonly rig: CharacterRig) {}

  update(a: AnimInput): void {
    this.last = a;
    const r = this.rig;
    const seated =
      a.pose === 'sit' ||
      a.pose === 'sitdrink' ||
      a.pose === 'rock' ||
      a.pose === 'ride' ||
      a.pose === 'armwrestle' ||
      a.pose === 'kneel' ||
      a.pose === 'scoot' ||
      a.pose === 'scootdrink';
    const moving = a.speed > 0.2 && !seated && a.pose !== 'fallen';
    const stride = Math.min(1, a.speed / 4.2);
    if (moving) this.phase += a.dt * a.speed * 2.3;
    const s = Math.sin(this.phase);

    let legL = 0;
    let legR = 0;
    let kneeL = 0;
    let kneeR = 0;
    let armL = Math.sin(a.time * 1.4) * 0.03;
    let armR = -armL;
    let armLz = 0.06;
    let armRz = -0.06;
    let lean = 0;
    let headX = 0;
    let bodyY = 0;
    let bodyX = 0;
    let legLz = 0;
    let legRz = 0;

    if (moving) {
      const amp = (a.sprinting ? 0.95 : 0.6) * stride;
      legL = s * amp;
      legR = -s * amp;
      kneeL = Math.max(0, -s) * 0.9 * stride;
      kneeR = Math.max(0, s) * 0.9 * stride;
      armL = -s * amp * 0.85;
      armR = s * amp * 0.85;
      bodyY = Math.abs(Math.cos(this.phase)) * 0.05 * stride;
      lean = a.sprinting ? 0.18 : 0.04;
    }
    if (a.airborne) {
      legL = -0.5;
      legR = 0.2;
      kneeL = 0.8;
      kneeR = 0.4;
      armL = -2.4;
      armR = -2.4;
      armLz = 0.5;
      armRz = -0.5;
    }

    switch (a.pose) {
      case 'sit':
      case 'sitdrink':
      case 'rock': {
        const seat = a.seat ?? 0.45;
        legL = legR = -1.45;
        kneeL = kneeR = 1.4;
        bodyY = seat + 0.06 - HIP + 0.05;
        armL = armR = -0.35;
        if (a.pose === 'rock') bodyX = Math.sin(a.time * 1.7) * 0.09 - 0.12;
        else lean = -0.12;
        if (a.pose === 'sitdrink') {
          // Sip, lower, sip again.
          const sip = Math.sin(a.time * 1.6) > 0.2;
          armR = sip ? -2.55 : -0.9;
          armRz = sip ? 0.35 : 0.1;
          headX = sip ? -0.3 : 0;
        }
        break;
      }
      case 'drink':
        armR = -2.55;
        armRz = 0.35;
        headX = -0.3;
        break;
      case 'eat':
        armR = -2.2;
        armRz = 0.45;
        headX = 0.1;
        break;
      case 'carry':
        armL = armR = -2.9;
        armLz = 0.15;
        armRz = -0.15;
        break;
      case 'push':
        armL = armR = -0.95;
        lean = 0.22;
        break;
      case 'wave':
        armR = -2.7;
        armRz = -0.4 + Math.sin(a.time * 9) * 0.35;
        break;
      case 'cheer':
        armL = -2.8 + Math.sin(a.time * 10) * 0.2;
        armR = -2.8 - Math.sin(a.time * 10) * 0.2;
        armLz = 0.35;
        armRz = -0.35;
        bodyY = Math.abs(Math.sin(a.time * 7)) * 0.12;
        break;
      case 'fallen':
        bodyX = -1.45;
        bodyY = 0.16 - HIP + 0.12;
        armL = armR = -2.8;
        legL = legR = 0;
        break;
      case 'aim':
        // Rifle shouldered, cheek on the stock.
        armR = -1.42;
        armRz = 0.32;
        armL = -1.62;
        armLz = -0.42;
        headX = 0.12;
        lean = 0.06;
        break;
      case 'ride': {
        // Straddling a bull: knees out, one hand on the rope, the other up for the crowd.
        const seat = a.seat ?? 0;
        legL = legR = -1.1;
        kneeL = kneeR = 1.5;
        legLz = -0.55;
        legRz = 0.55;
        bodyY = seat + 0.06 - HIP + 0.05;
        armL = -0.75;
        armLz = 0.1;
        armR = -2.9;
        armRz = -0.35 + Math.sin(a.time * 8) * 0.25;
        break;
      }
      case 'armwrestle': {
        // Seated at a table, elbow down, right hand up and forward.
        const seat = a.seat ?? 0.78;
        legL = legR = -1.45;
        kneeL = kneeR = 1.4;
        bodyY = seat + 0.06 - HIP + 0.05;
        lean = 0.32;
        armR = -1.3;
        armRz = 0.28;
        armL = -0.95;
        armLz = -0.15;
        break;
      }
      case 'kneel':
        // Down on the bank with an arm in the hole.
        legL = legR = 0.2;
        kneeL = kneeR = 2.3;
        bodyY = -0.42;
        lean = 0.75;
        armR = -0.55;
        armRz = 0.05;
        armL = -0.3;
        headX = -0.3;
        break;
      case 'throw':
        // Ready to throw: elbow up, dart (or bag) by the ear.
        armR = -2.2;
        armRz = 0.2;
        armL = -0.4;
        break;
      case 'scoot':
      case 'scootdrink': {
        // On the Rascal: feet on the floorboard, both hands on the tiller (or one on a beer).
        const seat = a.seat ?? 0.6;
        legL = legR = -1.5;
        kneeL = kneeR = 1.45;
        bodyY = seat + 0.06 - HIP + 0.05;
        lean = 0.05;
        armL = armR = -1.0;
        armLz = -0.05;
        armRz = 0.05;
        if (a.pose === 'scootdrink') {
          armR = -2.55;
          armRz = 0.35;
          headX = -0.3;
        }
        break;
      }
      case 'pull':
        // Leaning back, hauling on something.
        armL = armR = -1.25;
        armLz = 0.18;
        armRz = -0.18;
        lean = -0.3;
        legL = -0.35;
        kneeL = 0.5;
        break;
      default:
        break;
    }

    const k = 14;
    const dt = a.dt;
    r.legL.rotation.x = damp(r.legL.rotation.x, legL, k, dt);
    r.legR.rotation.x = damp(r.legR.rotation.x, legR, k, dt);
    r.legL.rotation.z = damp(r.legL.rotation.z, legLz, k, dt);
    r.legR.rotation.z = damp(r.legR.rotation.z, legRz, k, dt);
    r.kneeL.rotation.x = damp(r.kneeL.rotation.x, kneeL, k, dt);
    r.kneeR.rotation.x = damp(r.kneeR.rotation.x, kneeR, k, dt);
    r.armL.rotation.x = damp(r.armL.rotation.x, armL, k, dt);
    r.armR.rotation.x = damp(r.armR.rotation.x, armR, k, dt);
    r.armL.rotation.z = damp(r.armL.rotation.z, armLz, k, dt);
    r.armR.rotation.z = damp(r.armR.rotation.z, armRz, k, dt);
    r.torso.rotation.x = damp(r.torso.rotation.x, lean, 8, dt);
    r.head.rotation.x = damp(r.head.rotation.x, headX, 10, dt);
    r.body.position.y = damp(r.body.position.y, HIP + bodyY, 18, dt);
    r.body.rotation.x = damp(r.body.rotation.x, bodyX, 10, dt);
    r.can.visible = a.pose === 'drink' || a.pose === 'sitdrink' || a.pose === 'scootdrink';
    r.rifle.visible = a.pose === 'aim';

    // Crocs: heel strap swings back into sport mode while sprinting.
    this.sport = damp(this.sport, a.sprinting ? 1 : 0, 10, dt);
    for (const strap of r.straps) strap.rotation.x = this.sport * 2.05;
  }

  /** Directly set the hip-pivot flip angle (trampoline stunts). Call after update(). */
  setFlip(angle: number, height: number): void {
    this.rig.body.rotation.x = angle;
    this.rig.body.position.y = HIP + height;
  }
}
